import type { Grade, LifetimeStats, RoundStats } from './types.js';

export type Tier = 'bronze' | 'silver' | 'gold';

export type AchievementFamily = 'mastery' | 'speed' | 'persistence' | 'competition';

/** Display order in the trophy case: what you do first comes first. */
export const FAMILY_ORDER: AchievementFamily[] = ['mastery', 'speed', 'persistence', 'competition'];

/** What a badge is worth toward a student's badge score. */
export const TIER_POINTS: Record<Tier, number> = { bronze: 10, silver: 25, gold: 60 };

export interface AchievementCtx {
  round: RoundStats;
  lifetime: LifetimeStats;
  /** Rank in the session that just ended, 1-based. Null outside a session. */
  rank: number | null;
  playerCount: number;
  /** Rank at the previous round, for the comeback badge. */
  previousRank: number | null;
  grade: Grade;
}

/** How close a student is to a badge they have not earned yet. */
export interface Progress {
  have: number;
  need: number;
}

export interface Achievement {
  code: string;
  family: AchievementFamily;
  tier: Tier;
  /** One glyph for the medal face — the catalogue owns it so every surface agrees. */
  icon: string;
  /** i18n keys; the catalogue carries no display text of its own. */
  nameKey: string;
  descKey: string;
  earned: (ctx: AchievementCtx) => boolean;
  /**
   * Countable badges expose their progress so the trophy case can show "63/100"
   * rather than a locked silhouette — a bar you can watch move is worth far
   * more to a nine-year-old than a badge that simply appears one day.
   */
  progress?: (ctx: AchievementCtx) => Progress;
}

const p = (have: number, need: number): Progress => ({ have: Math.min(have, need), need });

/**
 * Evaluated on the server at round end, never on the client — a badge is a
 * durable claim on a student's profile, so it is granted where the score is.
 * Solo practice runs the same catalogue against a local profile.
 */
export const ACHIEVEMENTS: Achievement[] = [
  // ── mastery ──────────────────────────────────────────────────────────────
  { code: 'first_clear', family: 'mastery', tier: 'bronze', icon: '🎉',
    nameKey: 'ach.first_clear', descKey: 'ach.first_clear.d',
    earned: (c) => c.lifetime.boardsCleared >= 1,
    progress: (c) => p(c.lifetime.boardsCleared, 1) },
  { code: 'perfect_board', family: 'mastery', tier: 'silver', icon: '💎',
    nameKey: 'ach.perfect_board', descKey: 'ach.perfect_board.d',
    earned: (c) => c.round.cleared && c.round.misses === 0 },
  { code: 'no_add_clear', family: 'mastery', tier: 'silver', icon: '🧩',
    nameKey: 'ach.no_add_clear', descKey: 'ach.no_add_clear.d',
    earned: (c) => c.round.cleared && c.round.addsUsed === 0 },
  { code: 'unaided', family: 'mastery', tier: 'gold', icon: '🛡️',
    nameKey: 'ach.unaided', descKey: 'ach.unaided.d',
    earned: (c) => c.round.cleared && c.round.hintsUsed === 0 && c.round.addsUsed === 0 && c.round.misses === 0 },
  { code: 'grade_ace', family: 'mastery', tier: 'gold', icon: '🎓',
    nameKey: 'ach.grade_ace', descKey: 'ach.grade_ace.d',
    earned: (c) => c.lifetime.gradesAced.includes(c.grade) },

  // ── speed ────────────────────────────────────────────────────────────────
  { code: 'quick_off', family: 'speed', tier: 'bronze', icon: '⚡',
    nameKey: 'ach.quick_off', descKey: 'ach.quick_off.d',
    earned: (c) => c.round.matches >= 5 && c.round.elapsedMs <= 15_000 },
  { code: 'speed_demon', family: 'speed', tier: 'silver', icon: '🚀',
    nameKey: 'ach.speed_demon', descKey: 'ach.speed_demon.d',
    earned: (c) => c.round.cleared && c.round.elapsedMs <= 30_000 },
  { code: 'combo_5', family: 'speed', tier: 'bronze', icon: '🔥',
    nameKey: 'ach.combo_5', descKey: 'ach.combo_5.d',
    earned: (c) => c.round.maxCombo >= 5,
    progress: (c) => p(Math.max(c.round.maxCombo, c.lifetime.bestCombo), 5) },
  { code: 'combo_10', family: 'speed', tier: 'silver', icon: '🌟',
    nameKey: 'ach.combo_10', descKey: 'ach.combo_10.d',
    earned: (c) => c.round.maxCombo >= 10,
    progress: (c) => p(Math.max(c.round.maxCombo, c.lifetime.bestCombo), 10) },
  { code: 'combo_20', family: 'speed', tier: 'gold', icon: '☄️',
    nameKey: 'ach.combo_20', descKey: 'ach.combo_20.d',
    earned: (c) => c.round.maxCombo >= 20,
    progress: (c) => p(Math.max(c.round.maxCombo, c.lifetime.bestCombo), 20) },

  // ── persistence ──────────────────────────────────────────────────────────
  { code: 'century', family: 'persistence', tier: 'bronze', icon: '💯',
    nameKey: 'ach.century', descKey: 'ach.century.d',
    earned: (c) => c.lifetime.totalMatches >= 100,
    progress: (c) => p(c.lifetime.totalMatches, 100) },
  { code: 'marathon', family: 'persistence', tier: 'gold', icon: '🏔️',
    nameKey: 'ach.marathon', descKey: 'ach.marathon.d',
    earned: (c) => c.lifetime.totalMatches >= 1000,
    progress: (c) => p(c.lifetime.totalMatches, 1000) },
  { code: 'regular', family: 'persistence', tier: 'bronze', icon: '📘',
    nameKey: 'ach.regular', descKey: 'ach.regular.d',
    earned: (c) => c.lifetime.totalRounds >= 10,
    progress: (c) => p(c.lifetime.totalRounds, 10) },
  { code: 'streak_3', family: 'persistence', tier: 'silver', icon: '📆',
    nameKey: 'ach.streak_3', descKey: 'ach.streak_3.d',
    earned: (c) => c.lifetime.dayStreak >= 3,
    progress: (c) => p(c.lifetime.dayStreak, 3) },
  { code: 'streak_7', family: 'persistence', tier: 'gold', icon: '🗓️',
    nameKey: 'ach.streak_7', descKey: 'ach.streak_7.d',
    earned: (c) => c.lifetime.dayStreak >= 7,
    progress: (c) => p(c.lifetime.dayStreak, 7) },

  // ── competition ──────────────────────────────────────────────────────────
  { code: 'podium', family: 'competition', tier: 'bronze', icon: '🥉',
    nameKey: 'ach.podium', descKey: 'ach.podium.d',
    earned: (c) => c.rank !== null && c.rank <= 3 && c.playerCount >= 3 },
  { code: 'champion', family: 'competition', tier: 'silver', icon: '🥇',
    nameKey: 'ach.champion', descKey: 'ach.champion.d',
    earned: (c) => c.rank === 1 && c.playerCount >= 3 },
  { code: 'podium_5', family: 'competition', tier: 'gold', icon: '🏆',
    nameKey: 'ach.podium_5', descKey: 'ach.podium_5.d',
    earned: (c) => c.lifetime.podiums >= 5,
    progress: (c) => p(c.lifetime.podiums, 5) },
  { code: 'comeback', family: 'competition', tier: 'silver', icon: '📈',
    nameKey: 'ach.comeback', descKey: 'ach.comeback.d',
    earned: (c) =>
      c.rank !== null && c.previousRank !== null &&
      c.playerCount >= 4 && c.previousRank >= c.playerCount - 1 && c.rank <= 3 },
];

export const ACHIEVEMENT_BY_CODE = new Map(ACHIEVEMENTS.map((a) => [a.code, a]));

/** Newly earned codes only — anything already on the profile is filtered out. */
export function evaluate(ctx: AchievementCtx, alreadyHeld: ReadonlySet<string>): string[] {
  return ACHIEVEMENTS
    .filter((a) => !alreadyHeld.has(a.code) && a.earned(ctx))
    .map((a) => a.code);
}

/** Badge score: what the trophy case totals and the class board can rank on. */
export function badgeScore(codes: Iterable<string>): number {
  let total = 0;
  for (const code of codes) {
    const a = ACHIEVEMENT_BY_CODE.get(code);
    if (a) total += TIER_POINTS[a.tier];
  }
  return total;
}
